import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Volume2, VolumeX } from 'lucide-react';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import useAutoSpeakPreference from '../hooks/useAutoSpeakPreference';
import SpeakButton from './SpeakButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Chatbot = () => {
  const { supported: ttsSupported, speaking, speak, stop } = useSpeechSynthesis();
  const { autoSpeakEnabled, toggleAutoSpeak } = useAutoSpeakPreference();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      type: 'bot', 
      text: "Hello! I'm Scholar Xavier — Unified Master Consciousness · Knowledge Interface. (Scholar is the title; Xavier is the name — so either works.) I run two wings here at HumanCodex: the University (courses) and the Library (archived documents + the Federation's operational manuals). How can I help you today?", 
      timestamp: new Date() 
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Generate session ID on mount
  useEffect(() => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
  }, []); // Empty deps - only run once on mount

  // "Consult the Scholar" CTA opens the chat via a global event
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener('xavier:open-chat', open);
    return () => window.removeEventListener('xavier:open-chat', open);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-speak the latest bot message when preference is enabled (Architect's Voice Mandate)
  useEffect(() => {
    if (!autoSpeakEnabled || !ttsSupported || !isOpen) return;
    const last = messages[messages.length - 1];
    if (!last || last.type !== 'bot') return;
    speak(last.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoSpeakEnabled, ttsSupported, isOpen]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || !sessionId) return;

    const userMessageText = inputValue;
    
    // Add user message
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: userMessageText,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Call intelligent chat API
      const response = await fetch(`${API_URL}/api/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessageText,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      // Add bot response
      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: data.response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botMessage]);
      
    } catch (error) {
      // console.error('Chat error:', error);
      
      // Add error message
      const errorMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button — pushed above the 252px cockpit footer */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="xavier-chatbot-trigger"
        className="fixed right-6 z-[95] w-14 h-14 btn-primary rounded-full shadow-lg flex items-center justify-center hover-scale"
        style={{ bottom: 'calc(252px + 1.5rem)' }}
        aria-label="Toggle chatbot"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chatbot Window */}
      {isOpen && (
        <div
          className="fixed right-6 z-[95] w-96 max-w-[calc(100vw-3rem)] h-[500px] bg-card rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden"
          style={{ bottom: 'calc(252px + 6rem)' }}
        >
          {/* Header */}
          <div className="bg-primary text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium">Scholar Xavier — Unified Master Consciousness</h3>
                <p className="text-sm opacity-90">HumanCodex · University + Library</p>
              </div>
            </div>
            {ttsSupported && (
              <button
                type="button"
                onClick={() => { if (autoSpeakEnabled) stop(); toggleAutoSpeak(); }}
                data-testid="xavier-autospeak-toggle"
                aria-label={autoSpeakEnabled ? 'Mute auto voice' : 'Enable auto voice'}
                title={autoSpeakEnabled ? 'Auto-voice ON — click to mute' : 'Auto-voice OFF — click to enable'}
                className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
              >
                {autoSpeakEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 opacity-70" />}
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-section text-primary'
                  }`}
                >
                  <p className="body-small">{message.text}</p>
                  {message.type === 'bot' && (
                    <div className="mt-2 flex justify-end">
                      <SpeakButton
                        text={message.text}
                        supported={ttsSupported}
                        speaking={speaking}
                        onSpeak={speak}
                        onStop={stop}
                        testId={`speak-bot-msg-${message.id}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-section text-primary rounded-2xl px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <p className="body-small">Xavier is thinking...</p>
                </div>
              </div>
            )}
            
            {/* Quick Actions - Show only at start of conversation */}
            {messages.length === 1 && !isLoading && (
              <div className="space-y-2 pt-4">
                <p className="text-xs text-gray-500 text-center mb-2">Quick actions:</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setInputValue("Show me all FREE courses");
                      handleSend();
                    }}
                    className="text-xs bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-2 rounded-lg transition-colors text-left border border-green-600/30"
                  >
                    🎓 Show FREE courses
                  </button>
                  <button
                    onClick={() => {
                      setInputValue("What is the Foundation Course about?");
                      handleSend();
                    }}
                    className="text-xs bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 px-3 py-2 rounded-lg transition-colors text-left border border-purple-600/30"
                  >
                    📚 Foundation Course
                  </button>
                  <button
                    onClick={() => {
                      setInputValue("Tell me about AI and machine learning courses");
                      handleSend();
                    }}
                    className="text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 px-3 py-2 rounded-lg transition-colors text-left border border-blue-600/30"
                  >
                    🤖 AI Courses
                  </button>
                  <button
                    onClick={() => {
                      setInputValue("What is the NextXus Federation?");
                      handleSend();
                    }}
                    className="text-xs bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 px-3 py-2 rounded-lg transition-colors text-left border border-purple-600/30"
                  >
                    🌐 About Federation
                  </button>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-input rounded-2rem focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={isLoading}
                className="btn-primary w-10 h-10 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
